import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';

import {
    ConfigViewMetric,
    ConfigViewPillar,
    flatten,
    Metric,
} from './config.insight.model';
import {
    CustomizeMetricsInsightCanSelectFn,
    CustomizeMetricsInsightChangeEvent,
    NestedMetric,
} from './customize-metrics.insight.model';
import { MetricPath } from './insight.state';
import {
    NestedNode,
    NestedNodeSelectInsightChangeEvent,
} from './nested-node-select.insight.model';

function mapToNestedMetric(
    pillar: ConfigViewPillar,
    metric: ConfigViewMetric,
    lookup: MetricPath[],
    parentPair: MetricPath,
    depth: number,
): NestedMetric {
    const pair = lookup
        .filter((e) => e[2].length === depth + 1)
        .find(
            (e) =>
                e[0] === pillar &&
                e[1].metricId === metric.metricId &&
                (!parentPair ||
                    e[2][depth - 1].metricId === parentPair[1].metricId),
        );
    if (!pair) return null;
    return {
        pair,
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        components:
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            metric.components &&
            metric.components
                .map((e) =>
                    mapToNestedMetric(pillar, e, lookup, pair, depth + 1),
                )
                .filter((e) => !!e),
    };
}

function pairToId(pair: MetricPath): string {
    return `${pair[0].identifier}-${pair[2].reduce(
        (p, c) => `${p}-${c.metricId}`,
        '',
    )}`;
}

function mapToNode(nm: NestedMetric): NestedNode {
    return {
        id: pairToId(nm.pair),
        label: nm.pair[1].shortName,
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        children: nm.components && nm.components.map(mapToNode),
    };
}

function mapSelectionToNode(pair: MetricPath): NestedNode {
    return {
        id: pairToId(pair),
        label: null,
    };
}

function mapToMetric(node: NestedNode, lookup: MetricPath[]): MetricPath {
    return lookup.find((e) => pairToId(e) === node.id);
}

@Component({
    selector: 'customize-metrics-insight',
    templateUrl: './customize-metrics.insight.component.html',
})
export class CustomizeMetricsInsightComponent
    implements OnChanges, OnInit, OnDestroy
{
    @Input() metrics: Metric[];
    @Input() pillars: ConfigViewPillar[];
    @Input() selection: MetricPath[];
    @Input() canSelect: CustomizeMetricsInsightCanSelectFn;
    @Output() change: EventEmitter<CustomizeMetricsInsightChangeEvent>;

    _canSelect: any;

    localState: {
        metricsFlatten: MetricPath[];
        nodes: NestedNode[];
        selection: NestedNode[];
    };

    constructor() {
        this.change = new EventEmitter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.metrics != null ||
            changes.pillars != null ||
            changes.selection != null ||
            changes.canSelect != null
        ) {
            this._setValues();
        }
    }

    ngOnInit(): void {
        this._setValues();
    }

    ngOnDestroy(): void {
        this.change.complete();
    }

    _onChange(event: NestedNodeSelectInsightChangeEvent): void {
        this.change?.emit({
            selected: event.selected.map((ee) =>
                mapToMetric(ee, this.localState.metricsFlatten),
            ),
        });
    }

    private _setValues(): void {
        const metricsFlatten = flatten<ConfigViewPillar, MetricPath>(
            this.pillars,
            (i) =>
                flatten<
                    | ConfigViewMetric
                    | [ConfigViewMetric, ConfigViewMetric]
                    | any,
                    MetricPath
                >(
                    i.metrics,
                    (ii) => {
                        if (Array.isArray(ii)) {
                            const m = this.metrics.find(
                                (ee) => ee.metricId === ii[1].metricId,
                            );
                            return [[i, m, [ii[0], ii[1]]] as MetricPath];
                        } else {
                            const m = this.metrics.find(
                                (ee) => ee.metricId === ii.metricId,
                            );
                            return [[i, m, [ii]] as MetricPath];
                        }
                    },
                    (ii) =>
                        !Array.isArray(ii) && ii.components
                            ? ii.components.map((e) => [ii, e])
                            : null,
                ),
            (i) => i.pillars,
        ).filter((e) => e[1]);

        const nestedMetrics = flatten(
            this.pillars,
            (i) =>
                i.metrics
                    .map((e) =>
                        mapToNestedMetric(i, e, metricsFlatten, null, 0),
                    )
                    .filter((e) => !!e),
            (i) => i.pillars,
        );

        const nodes = nestedMetrics.map(mapToNode);
        const selection = this.selection.map(mapSelectionToNode);
        this.localState = { metricsFlatten, nodes, selection };
        this._canSelect = (node, checked) =>
            this.canSelect?.(
                mapToMetric(node, this.localState.metricsFlatten),
                checked,
            ) ?? true;
    }
}
